import React, { useEffect, useState } from "react";
import axios from "axios";
import "./Users.css";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false); // no initial loading
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    fullName: "",
    phoneNumber: "",
    flatNumber: "",
    societyName: "",
  });

  // 🔹 New state for societies dropdown
  const [societies, setSocieties] = useState([]);
  const [selectedSociety, setSelectedSociety] = useState("");

  // 🔹 Fetch societies for dropdown
  useEffect(() => {
    const fetchSocieties = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get("/api/admin/names", {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        });
        setSocieties(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch societies.");
      }
    };
    fetchSocieties();
  }, []);

  // 🔹 Fetch users by selected society
  const fetchUsersBySociety = async (societyName) => {
    if (!societyName) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        "/api/admin/usersbysocietyname",
        {
          params: { societyName },
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        }
      );
      const data = Array.isArray(res.data) ? res.data : [];
      setUsers(data);
      setFilteredUsers(data);
      setError("");
    } catch (err) {
      console.error(err);
      setError("Failed to fetch users.");
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Handle society selection
  const handleSocietyChange = (e) => {
    setSelectedSociety(e.target.value);
  };

  // 🔹 Handle fetch button click
  const handleFetchUsers = () => {
    fetchUsersBySociety(selectedSociety);
  };

  // 🔍 Search Logic
  useEffect(() => {
    const lowerQuery = searchQuery.toLowerCase();
    const filtered = users.filter(
      (u) =>
        u.fullName?.toLowerCase().includes(lowerQuery) ||
        u.phoneNumber?.toLowerCase().includes(lowerQuery) ||
        u.flatNumber?.toLowerCase().includes(lowerQuery) ||
        u.societyName?.toLowerCase().includes(lowerQuery)
    );
    setFilteredUsers(filtered);
  }, [searchQuery, users]);

  // ✏️ Open Edit Modal
  const handleEditClick = (user) => {
    setEditingUser(user);
    setFormData({
      fullName: user.fullName,
      phoneNumber: user.phoneNumber,
      flatNumber: user.flatNumber || "",
      societyName: user.societyName || "",
    });
  };

  // 🧾 Input Change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 💾 Save Edited User
  const handleSave = async () => {
    try {
      const token = localStorage.getItem("token");
      const updatedData = {
        ...editingUser,
        fullName: formData.fullName,
        phoneNumber: formData.phoneNumber,
        flatNumber: formData.flatNumber,
        societyName: formData.societyName,
      };

      await axios.put(
        `/api/admin/users/${editingUser.id}`,
        updatedData,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      setUsers((prev) =>
        prev.map((u) => (u.id === editingUser.id ? updatedData : u))
      );
      setFilteredUsers((prev) =>
        prev.map((u) => (u.id === editingUser.id ? updatedData : u))
      );

      setEditingUser(null);
      alert("User updated successfully ✅");
    } catch (err) {
      console.error(err);
      alert("Failed to update user ❌");
    }
  };

  // 🔄 Toggle Active/Inactive
  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem("token");
      const newStatus = !currentStatus;

      await axios.put(
        `/api/admin/users/status/${userId}`,
        { enabled: newStatus },
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        }
      );

      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, enabled: newStatus } : u
        )
      );
      setFilteredUsers((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, enabled: newStatus } : u
        )
      );

      alert(
        `User ${newStatus ? "activated ✅" : "deactivated 🚫"} successfully`
      );
    } catch (err) {
      console.error(err);
      alert("Failed to change status ❌");
    }
  };

  // 🗑️ Delete User
  const handleDeleteUser = async (userId) => {
    if (
      !window.confirm(
        "⚠️ Are you sure you want to delete this user? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/admin/users/${userId}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });

      setUsers((prev) => prev.filter((u) => u.id !== userId));
      setFilteredUsers((prev) => prev.filter((u) => u.id !== userId));

      alert("User deleted successfully 🗑️");
    } catch (err) {
      console.error(err);
      alert("Failed to delete user ❌");
    }
  };

  // 🔐 Reset Password
  const handleResetPassword = async (userId) => {
    const newPassword = prompt("Enter new password for this user:");

    if (!newPassword) {
      alert("Password cannot be empty ❌");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const res = await axios.put(
        `/api/admin/reset-password`,
        {},
        {
          params: { userId, newPassword },
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      alert(res.data || "Password updated successfully ✅");
    } catch (err) {
      console.error(err);
      alert("Failed to update password ❌");
    }
  };

  return (
    <div className="users-page">
      <h2>👥 Manage Users</h2>
      <p>View, search, and edit user accounts below.</p>

      {/* 🔹 Society dropdown */}
      <div className="dropdown-section">
        <select value={selectedSociety} onChange={(e) => setSelectedSociety(e.target.value)}>
          <option value="">-- Select Society --</option>
          {societies.map((soc, idx) => (
            <option key={idx} value={soc}>
              {soc}
            </option>
          ))}
        </select>
        <button onClick={handleFetchUsers} disabled={!selectedSociety}>
          Fetch Users
        </button>
      </div>

      {/* 🔍 Search Bar */}
      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by name, phone, flat, or society..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {loading ? (
        <p className="loading">Loading users...</p>
      ) : error ? (
        <p className="error">{error}</p>
      ) : (
        <div className="table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Full Name</th>
                <th>Phone Number</th>
                <th>Flat Number</th>
                <th>Society Name</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((u) => (
                  <tr key={u.id}>
                    <td>{u.id}</td>
                    <td>{u.fullName}</td>
                    <td>{u.phoneNumber}</td>
                    <td>{u.flatNumber || "-"}</td>
                    <td>{u.societyName || "-"}</td>
                    <td
                      className={u.enabled ? "status-active" : "status-disabled"}
                    >
                      {u.enabled ? "Active" : "Inactive"}
                    </td>
                    <td>
                      <button
                        className="edit-btn"
                        onClick={() => handleEditClick(u)}
                      >
                        Edit
                      </button>
                      <button
                        className={`status-btn ${u.enabled ? "deactivate" : "activate"}`}
                        onClick={() => handleToggleStatus(u.id, u.enabled)}
                      >
                        {u.enabled ? "Deactivate" : "Activate"}
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteUser(u.id)}
                      >
                        Delete
                      </button>
                      <button
                        className="reset-btn"
                        onClick={() => handleResetPassword(u.id)}
                      >
                        Password
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center", color: "#bbb" }}>
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ✏️ Edit Modal */}
      {editingUser && (
        <div className="edit-modal">
          <div className="modal-content">
            <h3>Edit User</h3>
            <input
              type="text"
              name="fullName"
              placeholder="Full Name"
              value={formData.fullName}
              onChange={handleChange}
            />
            <input
              type="text"
              name="phoneNumber"
              placeholder="Phone Number"
              value={formData.phoneNumber}
              onChange={handleChange}
            />
            <input
              type="text"
              name="flatNumber"
              placeholder="Flat Number"
              value={formData.flatNumber}
              onChange={handleChange}
            />
            <input
              type="text"
              name="societyName"
              placeholder="Society Name"
              value={formData.societyName}
              onChange={handleChange}
            />
            <div className="modal-actions">
              <button onClick={handleSave}>Save</button>
              <button onClick={() => setEditingUser(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
